import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Globe, Clock, Monitor, LogOut, Ban, CheckCircle2, CalendarDays, Mail, StickyNote, AlertTriangle, Flag, Send, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLoading } from "../context/LoadingContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { FaDiscord } from "react-icons/fa";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-800/60 bg-slate-900/40 px-4 py-3">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-purple-400" />
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="truncate text-sm text-slate-200">{value || "—"}</p>
    </div>
  </div>
);

export default function UserDetailDrawer({ open, onOpenChange, userType, userId, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("note");
  const { hasPerm } = useAuth();
  const { withLoading } = useLoading();

  const load = async () => {
    if (!userId) return;
    setDetail(null);
    const path = userType === "staff" ? `/staff/${userId}` : `/customers/${userId}`;
    const res = await api.get(path);
    setDetail(res.data);
  };

  useEffect(() => { if (open && userId) load(); }, [open, userId]);

  const signOut = async (sid) => {
    await withLoading(api.post(`/sessions/${sid}/signout`), "Signing out session");
    toast.success("Session signed out");
    load();
  };

  const toggleSuspend = async () => {
    const path = userType === "staff" ? `/staff/${userId}/suspend` : `/customers/${userId}/suspend`;
    await withLoading(api.post(path, { suspended: !detail.suspended }), detail.suspended ? "Restoring account" : "Suspending account");
    toast.success(detail.suspended ? "Account restored" : "Account suspended");
    await load();
    onChanged?.();
  };

  const addNote = async () => {
    if (!noteText.trim()) return toast.error("Write something first");
    await withLoading(api.post(`/users/${userType}/${userId}/notes`, { type: noteType, content: noteText.trim() }), "Saving note");
    toast.success(noteType === "warning" ? "Warning issued" : "Note added");
    setNoteText(""); setNoteType("note");
    load();
  };

  const deleteNote = async (nid) => {
    await withLoading(api.delete(`/notes/${nid}`), "Deleting");
    load();
  };

  const name = detail && (userType === "staff" ? `${detail.first_name} ${detail.last_name}` : detail.discord_username);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto border-slate-800 bg-[#0C0F16] sm:max-w-lg" data-testid="user-detail-drawer">
        <SheetHeader>
          <SheetTitle className="text-white">Profile</SheetTitle>
        </SheetHeader>
        {!detail ? (
          <div className="flex h-64 items-center justify-center text-slate-500">Loading...</div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 space-y-5">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-purple-500/40">
                <AvatarImage src={detail.avatar_url || detail.discord_avatar} />
                <AvatarFallback className="bg-purple-600/30 text-purple-200">{name?.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-display text-xl font-bold text-white">{name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {detail.discord_in_server ? (
                    <span className="inline-flex items-center gap-1 rounded border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-xs text-indigo-300"><FaDiscord /> In Server</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-0.5 text-xs text-slate-400"><FaDiscord /> Not in Server</span>
                  )}
                  {detail.suspended && <span className="rounded border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-xs text-red-400">Suspended</span>}
                  {detail.flagged && <span className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-400" data-testid="repeat-absentee-flag"><Flag className="h-3 w-3" /> Repeat Absentee · {detail.absence_count} absences</span>}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {userType === "staff" ? (
                <>
                  <Row icon={Mail} label="Workspace Email" value={detail.workspace_email} />
                  <Row icon={Mail} label="Personal Email" value={detail.personal_email} />
                </>
              ) : (
                <Row icon={Mail} label="Discord Email" value={detail.discord_email} />
              )}
              <Row icon={FaDiscord} label="Discord" value={detail.discord_username} />
              <Row icon={Clock} label="Last Sign In" value={detail.last_sign_in ? new Date(detail.last_sign_in).toLocaleString() : "Never"} />
              <Row icon={MapPin} label="Location" value={detail.last_location} />
              <Row icon={Globe} label="IP Address" value={detail.last_ip} />
            </div>

            <div>
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><CalendarDays className="h-4 w-4 text-purple-400" /> Participated Events ({detail.participated_events?.length || 0})</p>
              {detail.participated_events?.length ? (
                <div className="space-y-2">
                  {detail.participated_events.map((e) => (
                    <div key={e.id} className="flex items-center justify-between rounded-lg border border-slate-800/60 bg-slate-900/40 px-3 py-2 text-sm">
                      <span className="text-slate-200">{e.title}</span>
                      <span className={`rounded px-2 py-0.5 text-xs capitalize ${e.status === "present" ? "bg-emerald-500/10 text-emerald-400" : e.status === "absent" ? "bg-red-500/10 text-red-400" : "bg-slate-700/40 text-slate-400"}`}>{e.status}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-500">No events yet.</p>}
            </div>

            <div>
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Monitor className="h-4 w-4 text-purple-400" /> Active Sessions ({detail.sessions?.length || 0})</p>
              <div className="space-y-2">
                {detail.sessions?.length ? detail.sessions.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-800/60 bg-slate-900/40 px-3 py-2">
                    <div className="min-w-0 text-xs">
                      <p className="truncate text-slate-200">{s.location} · {s.ip}</p>
                      <p className="truncate text-slate-500">{s.user_agent}</p>
                    </div>
                    {hasPerm("sign_out_sessions") && (
                      <Button data-testid={`signout-session-${s.id}`} size="sm" variant="ghost" onClick={() => signOut(s.id)} className="shrink-0 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                        <LogOut className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )) : <p className="text-sm text-slate-500">No active sessions.</p>}
              </div>
            </div>

            {hasPerm("suspend_accounts") && (
              <Button
                data-testid="toggle-suspend-btn"
                onClick={toggleSuspend}
                className={`w-full gap-2 ${detail.suspended ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}`}
              >
                {detail.suspended ? <><CheckCircle2 className="h-4 w-4" /> Restore Account</> : <><Ban className="h-4 w-4" /> Suspend Account</>}
              </Button>
            )}

            <div>
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><StickyNote className="h-4 w-4 text-purple-400" /> Notes & Warnings ({detail.notes?.length || 0})</p>

              {hasPerm("manage_notes") && (
                <div className="mb-3 rounded-xl border border-slate-800/60 bg-slate-900/40 p-3">
                  <div className="mb-2 flex gap-2">
                    <Button data-testid="note-type-note" size="sm" onClick={() => setNoteType("note")} className={`h-7 gap-1 ${noteType === "note" ? "bg-purple-600 hover:bg-purple-500" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}><StickyNote className="h-3.5 w-3.5" /> Note</Button>
                    <Button data-testid="note-type-warning" size="sm" onClick={() => setNoteType("warning")} className={`h-7 gap-1 ${noteType === "warning" ? "bg-amber-600 hover:bg-amber-500" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}><AlertTriangle className="h-3.5 w-3.5" /> Warning</Button>
                  </div>
                  <Textarea data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder={noteType === "warning" ? "Describe the warning (e.g. 3 no-shows in a row)..." : "Add a private note about this member..."} className="min-h-[70px] bg-slate-900/60 text-sm" />
                  <Button data-testid="add-note-btn" size="sm" onClick={addNote} className="mt-2 w-full gap-1 bg-purple-600 hover:bg-purple-500"><Send className="h-3.5 w-3.5" /> {noteType === "warning" ? "Issue Warning" : "Add Note"}</Button>
                </div>
              )}

              <div className="space-y-2">
                {detail.notes?.length ? detail.notes.map((n) => (
                  <div key={n.id} className={`rounded-xl border px-3 py-2 ${n.type === "warning" ? "border-amber-500/30 bg-amber-500/5" : "border-slate-800/60 bg-slate-900/40"}`} data-testid={`note-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${n.type === "warning" ? "bg-amber-500/15 text-amber-400" : "bg-slate-700/50 text-slate-300"}`}>
                          {n.type === "warning" ? <AlertTriangle className="h-3 w-3" /> : <StickyNote className="h-3 w-3" />} {n.type}
                        </span>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-200">{n.content}</p>
                        <p className="mt-1 text-[11px] text-slate-500">{n.author_name} · {new Date(n.created_at).toLocaleString()}</p>
                      </div>
                      {hasPerm("manage_notes") && (
                        <Button data-testid={`delete-note-${n.id}`} size="sm" variant="ghost" onClick={() => deleteNote(n.id)} className="shrink-0 text-slate-500 hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                  </div>
                )) : <p className="text-sm text-slate-500">No notes yet.</p>}
              </div>
            </div>
          </motion.div>
        )}
      </SheetContent>
    </Sheet>
  );
}
